import React from 'react';
import { Bot, Boxes, Rocket } from 'lucide-react';
import { CodeBlock } from './CodeBlock';

export type LlmGuideId = 'agent' | 'models' | 'deploy';

export const LLM_GUIDES: Record<LlmGuideId, { title: string; summary: string; markdown: string }> = {
  agent: {
    title: 'Work with a coding agent',
    summary: 'Inspect a repo, make a bounded change, test it, open a PR.',
    markdown: `Read https://app.nz/llms-full.txt, especially “Launch a cloud coding agent”.

Use app.nz to complete this outcome: <describe the outcome>.

- Repository: <owner/repo>
- Constraints: preserve unrelated work; never expose secrets; ask before destructive or costly actions.
- Execution: choose an appropriate agent/model route, inspect existing instructions, implement the smallest coherent change, and run relevant tests.
- Visual changes: capture desktop and mobile evidence in VisualBench and inspect it.
- Return: summary, changed files, tests, remaining risks, and PR/deploy URL when created.`,
  },
  models: {
    title: 'Integrate or route a model',
    summary: 'Pick a route, keep OpenAI-compatible code, verify cost and fallback.',
    markdown: `Read https://app.nz/llms-full.txt, especially “Call any model”.

Integrate this workload through the app.nz gateway: <describe the workload>.

- Start with model app/auto; pin provider/model only when the workload requires it.
- Use base URL https://app.nz/v1 and read APP_API_KEY from the environment.
- Do not print, log, or commit API keys.
- Validate one successful request, structured error handling, timeout behavior, and streaming if used.
- Compare the chosen route with one reasonable fallback for quality, latency, and estimated cost.
- Return: runnable code, setup commands, selected route, fallback, and verification results.`,
  },
  deploy: {
    title: 'Build and ship an app',
    summary: 'Ship a tested app.nz deploy with logs and a live URL.',
    markdown: `Read https://app.nz/llms-full.txt, especially “Deploy and publish”.

Build and ship this application on app.nz: <describe the application>.

- Inspect the repository and its AGENTS.md before editing.
- Add or verify appnz.yaml; use a static runtime unless a server process is required.
- Keep secrets in app.nz environment configuration, never in source or terminal output.
- Run unit and end-to-end tests before deployment.
- For UI work, capture and inspect desktop and mobile VisualBench screenshots.
- Deploy with the app CLI, verify health and the public URL, then report logs, URL, and rollback notes.`,
  },
};

const guideIcons = { agent: Bot, models: Boxes, deploy: Rocket };

export function LlmQuickstart({ className = '' }: { className?: string }) {
  const [selected, setSelected] = React.useState<LlmGuideId>('agent');
  const guide = LLM_GUIDES[selected];

  return (
    <section className={`min-w-0 rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-5 ${className}`}>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-200/55">Quick copy for any LLM</p>
          <h2 className="mt-1 font-display text-xl font-bold">Markdown briefs for agents.</h2>
        </div>
        <a href="/llms-full.txt" className="text-xs font-mono text-white/55 underline decoration-white/25 hover:text-white">
          Raw Markdown guide ↗
        </a>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-3" role="tablist" aria-label="LLM instruction type">
        {(Object.keys(LLM_GUIDES) as LlmGuideId[]).map((id) => {
          const item = LLM_GUIDES[id];
          const Icon = guideIcons[id];
          const active = id === selected;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setSelected(id)}
              className={`rounded-lg border p-3 text-left transition ${active ? 'border-cyan-300/40 bg-cyan-300/[0.08]' : 'border-white/10 bg-black/45 hover:border-white/25'}`}
            >
              <span className="flex items-center gap-2 text-sm font-bold"><Icon size={15} /> {item.title}</span>
              <span className="mt-1 block text-xs leading-5 text-white/45">{item.summary}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-3" role="tabpanel">
        <CodeBlock code={guide.markdown} language="markdown" testId={`llm-guide-${selected}`} preClassName="!text-[13px] !leading-6 text-white/78" />
      </div>
      <p className="mt-3 text-xs text-white/42">
        Also available offline as <code className="font-mono text-white/68">app guide {selected}</code> and through the app.nz MCP server.
      </p>
    </section>
  );
}

